/**
 * The one row shape every import format maps onto, plus the sanitisation and
 * identity rules shared by all three (KTD9, KTD10).
 *
 * Nothing here does I/O: the file's bytes never leave the browser (KTD1).
 */

export type IdentitySystem = "linkedin" | "email";

export interface ContactIdentity {
	readonly system: IdentitySystem;
	readonly value: string;
}

export interface ParsedContact {
	/** 1-based index within the file's data rows; stable for the wizard's life. */
	readonly rowId: number;
	readonly name: string;
	readonly organisation?: string | undefined;
	readonly position?: string | undefined;
	/** Display values, unnormalised. First becomes `email`, then `email2`, `email3`. */
	readonly emails: string[];
	readonly phones: string[];
	readonly location?: string | undefined;
	/** Seed-entry text. */
	readonly note?: string | undefined;
	/** Seed-entry date as `YYYY-MM-DD`. */
	readonly date?: string | undefined;
	/** Tags, whitespace already hyphenated. */
	readonly labels: string[];
	/** External identity (KTD9); absent when the row has no email or profile URL. */
	readonly identity?: ContactIdentity | undefined;
	/** Lowercased, whitespace-collapsed name — the fallback match key. */
	readonly nameKey: string;
	/** The source's own record id (vCard `UID`), kept as attribute `vcardUid`. */
	readonly sourceRef?: string | undefined;
}

/**
 * The server's write limits, mirrored here so a parsed file can never fail
 * the commit schema. The email and phone caps keep a hostile card well under
 * the 100-attribute ceiling.
 */
export const LIMITS = {
	name: 256,
	value: 1000,
	note: 1000,
	tag: 256,
	tags: 20,
	emails: 3,
	phones: 3,
} as const;

/**
 * C0 controls other than tab and newline, DEL, and the Unicode bidi
 * overrides and isolates (U+202A-U+202E, U+2066-U+2069), which can make a
 * pasted name render as something other than what it stores.
 */
const isUnsafe = (code: number): boolean =>
	(code < 0x20 && code !== 0x09 && code !== 0x0a) ||
	code === 0x7f ||
	(code >= 0x202a && code <= 0x202e) ||
	(code >= 0x2066 && code <= 0x2069);

const stripUnsafe = (value: string): string =>
	[...value]
		.filter((character) => !isUnsafe(character.codePointAt(0) ?? 0))
		.join("");

const truncate = (value: string, max: number): string => {
	if (value.length <= max) return value;
	const cut = value.slice(0, max);
	const last = cut.charCodeAt(max - 1);
	// Never leave a lone high surrogate behind.
	return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
};

/** Strips unsafe characters, collapses horizontal runs, and truncates. */
export const clean = (value: string, max: number): string =>
	truncate(
		stripUnsafe(value)
			.replace(/[ \t]+/g, " ")
			.trim(),
		max,
	);

export const nameKeyOf = (name: string): string =>
	name.toLowerCase().replace(/\s+/g, " ").trim();

/**
 * The comparison form of an email (KTD9). Gmail ignores dots and `+tags`;
 * every other host does not, so only Gmail gets that treatment. The raw
 * value is kept separately for display.
 *
 * Rejects exactly what the server's `normalizeEmail` rejects — no `@`, an `@`
 * first or last, or a local part Gmail's folding empties. The two must agree
 * or a junk address becomes an identity the server's match index cannot see.
 */
export const normaliseEmail = (raw: string): string | undefined => {
	const trimmed = raw.trim().toLowerCase();
	const at = trimmed.lastIndexOf("@");
	if (at <= 0 || at === trimmed.length - 1) return undefined;
	const host = trimmed.slice(at + 1);
	const domain = host === "googlemail.com" ? "gmail.com" : host;
	const local = trimmed.slice(0, at);
	const bare =
		domain === "gmail.com"
			? (local.split("+")[0] ?? "").replace(/\./g, "")
			: local;
	return bare === "" ? undefined : `${bare}@${domain}`;
};

/** Within-file dedupe key (R4): the identity, or the name when there is none. */
export const dedupeKey = (contact: ParsedContact): string =>
	contact.identity
		? `${contact.identity.system}:${contact.identity.value}`
		: `name:${contact.nameKey}`;

export interface ContactFields {
	readonly name: string;
	readonly organisation?: string | undefined;
	readonly position?: string | undefined;
	readonly emails?: readonly string[] | undefined;
	readonly phones?: readonly string[] | undefined;
	readonly location?: string | undefined;
	readonly note?: string | undefined;
	readonly date?: string | undefined;
	readonly labels?: readonly string[] | undefined;
	/** Only LinkedIn supplies one; otherwise the first email becomes the identity. */
	readonly identity?: ContactIdentity | undefined;
	readonly sourceRef?: string | undefined;
}

const list = (values: readonly string[] | undefined, max: number): string[] =>
	[...new Set((values ?? []).map((value) => clean(value, LIMITS.value)))]
		.filter((value) => value !== "")
		.slice(0, max);

const optional = (
	value: string | undefined,
	max: number,
): string | undefined => {
	const cleaned = value === undefined ? "" : clean(value, max);
	return cleaned === "" ? undefined : cleaned;
};

/** No usable email means no identity at all, never a junk one. */
const emailIdentity = (
	email: string | undefined,
): ContactIdentity | undefined => {
	const value = email === undefined ? undefined : normaliseEmail(email);
	return value === undefined ? undefined : { system: "email", value };
};

/** Builds a sanitised, truncated contact and derives its identity (KTD9). */
export const makeContact = (
	rowId: number,
	fields: ContactFields,
): ParsedContact => {
	const name = clean(fields.name, LIMITS.name);
	const emails = list(fields.emails, LIMITS.emails);
	const first = emails[0];
	return {
		rowId,
		name,
		nameKey: nameKeyOf(name),
		organisation: optional(fields.organisation, LIMITS.value),
		position: optional(fields.position, LIMITS.value),
		emails,
		phones: list(fields.phones, LIMITS.phones),
		location: optional(fields.location, LIMITS.value),
		note: optional(fields.note, LIMITS.note),
		date: fields.date,
		labels: [
			...new Set(
				(fields.labels ?? [])
					.map((label) => clean(label, LIMITS.tag).replace(/\s+/g, "-"))
					.filter((label) => label !== ""),
			),
		].slice(0, LIMITS.tags),
		identity: fields.identity ?? emailIdentity(first),
		sourceRef: optional(fields.sourceRef, LIMITS.value),
	};
};
